import {
  Alert,
} from '@mui/material';
import { useQuery, useSubscription } from '@apollo/client';
import React, { useEffect, useCallback, useState, useContext, useRef } from 'react';
import { isEmpty } from 'lodash';
import { GET_ACTIVE_RECEIVED_REFERRALS } from '../gql';
import ActiveReceivedReferralsTable from '../tables/ActiveReceivedReferralsTable';
import { usePromoteReferral } from '../../referral/redux/promoteReferral';
import Swal from 'sweetalert2';
import Referral from '../../referral/models/Referral';
import clientGraphql from '../../../common/apollo-graphql';
import AuthContext from '../../auth/context/AuthContext';
import { REFERRAL_RECEIVER_SUBSCRIPTION } from '../../referral/gql/Subscription';
import SetAppointmentReferralDialog from '../dialogs/SetAppointmentReferralDialog';
import UpdateMiniReferralDialog from '../dialogs/UpdateMiniReferralDialog';
import UpdateReferrerClinicDialog from '../dialogs/UpdateReferrerClinicDialog';
import DialogReferralNotes from '../dialogs/DialogReferralNotes';
import GlobalLoader from '../../common/components/loader/GlobalLoader';
import moment from 'moment';
import ReferralCompleteDialog from '../dialogs/ReferralCompleteDialog';

export default function ActiveReceivedReferrals({ history, uuid }) {
  const currentUser = useContext(AuthContext);
  const { promoteReferral } = usePromoteReferral();

  // ---------------------------
  // STATE
  // ---------------------------
  const [parsedData, setParsedData] = useState({ rows: [], count: 0 });
  const [searchFilters, setSearchFilters] = useState({
    type: 'Name',
    patientName: '',
    birthdate: '',
  });

  const [referralUuid, setReferralUuid] = useState('');
  const [referral, setReferral] = useState(null);
  const [status, setStatus] = useState('');
  const [scheduleAppointmentDate, setScheduleAppointmentDate] = useState('');
  const [openNotes, setOpenNotes] = useState(false);
  const [openAppointmentDialog, setOpenAppointmentDialog] = useState(false);
  const [openReferralDialog, setOpenReferralDialog] = useState(false);
  const [openReferrerClinicDialog, setOpenReferrerClinicDialog] = useState(false);
  const [closeError, setCloseError] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);

  const shouldSubscribe = true;
  const referralsSubscribeRef = useRef([]);
  const debounceRef = useRef(null);

  let refetchData;

  // ---------------------------
  // QUERY / SUBSCRIPTION
  // ---------------------------
  const { loading, data: receivedReferrals, error, refetch } = useQuery(
    GET_ACTIVE_RECEIVED_REFERRALS,
    {
      client: clientGraphql,
      variables: {
        uuid,
        patientName: null,
        birthdate: null,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !uuid || Object.keys(uuid).length === 0,
    }
  );

  const { data: referrals } = useSubscription(REFERRAL_RECEIVER_SUBSCRIPTION, {
    variables: {
      clinic_id: !isEmpty(currentUser) && !isEmpty(currentUser.clinics) ? currentUser.clinics[0].id : -1
    },
    skip: !shouldSubscribe,
    lazy: true,
  });

  // ---------------------------
  // FILTER HANDLERS
  // ---------------------------
  const handleFilterChanged = (e) => {
    const value = e.target.value;
    const newFilters = { ...searchFilters };
    if (searchFilters.type === 'Name') newFilters.patientName = value;
    else newFilters.birthdate = value;

    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  const handleChangeDropdown = (e) => {
    const newFilters = { type: e.target.value, patientName: '', birthdate: '' };
    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  // ---------------------------
  // REFERRAL ACTIONS
  // ---------------------------
  const handleOnViewNotes = useCallback((rowId) => {
    setReferralUuid(rowId);
    setOpenNotes(true);
  }, []);

  const handleOnReview = useCallback((rowId, row) => {
    history.push(`/referrals/${row.id}/review`);
  }, [history]);

  const onPromoteReferral = useCallback((id, data) => {
    promoteReferral(id, 'rec_completed', data)
        .then(() => {
          Swal.fire({ icon: 'success', text: `Referral Completed`, showConfirmButton: true })
            .then(() => refetchData(searchFilters));
        })
        .catch(() => Swal.fire({ icon: 'error', text: 'Failed to complete referral!', showConfirmButton: true }));
    }, [promoteReferral, refetchData, searchFilters]);

  const handleOnComplete = useCallback((item) => {
    setReferralUuid(item.id);
    setAppointmentDate(item?.appointment?.appointment_date ? moment(item.appointment.appointment_date.replace('T', ' ')).format('DD MMM YYYY - HH:mm:ss') : '');
    if (item.status !== 'declined') {
      setOpenCompleteDialog(true);
    } else {
      promoteReferral(item.id, 'rec_completed', 'Referral Completed');
    }
  }, [promoteReferral]);

  const handleSetAppointment = useCallback((rowId, appointment, status) => {
    setScheduleAppointmentDate(appointment?.appointment_date);
    setReferralUuid(rowId);
    setStatus(status);
    setOpenAppointmentDialog(true);
  }, []);

  const handleUpdateReferral = useCallback((referral) => {
    setReferral(referral);
    setOpenReferralDialog(true);
  }, []);

  const handleUpdateReferrerClinic = useCallback((referral) => {
    setReferral(referral);
    setOpenReferrerClinicDialog(true);
  }, []);


  // ---------------------------
  // REFETCH + PARSE
  // ---------------------------
  refetchData = useCallback((filters) => {
    if (!refetch) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      refetch({
        uuid,
        patientName: filters.patientName || null,
        birthdate: filters.birthdate || null,
      }).then(({ data }) => {
        if (!isEmpty(data) && !isEmpty(data.rows)) {
          setParsedData({
            rows: data.rows.map(item => ({
              ...item,
              user: currentUser,
              referral: new Referral(item),
              actions: {
                onViewNotes: () => handleOnViewNotes(item.id),
                onReview: () => handleOnReview(item.id, item),
                onComplete: () => handleOnComplete(item),
                onSetAppointment: () => handleSetAppointment(item.id, item.appointment, item.status),
                onUpdateReferral: () => handleUpdateReferral(item),
                onUpdateReferrerClinic: () => handleUpdateReferrerClinic(item),
              },
            })),
            count: data.rows.length,
          });
        } else {
          setParsedData({ rows: [], count: 0 });
        }
      });
    }, 400);
  }, [
    refetch,
    uuid,
    currentUser,
    handleOnViewNotes,
    handleOnReview,
    handleOnComplete,
    handleSetAppointment,
    handleUpdateReferral,
    handleUpdateReferrerClinic,
  ]);

  // ---------------------------
  // INITIAL DATA LOAD
  // ---------------------------
  useEffect(() => {
    if (!isEmpty(receivedReferrals) && !isEmpty(receivedReferrals.rows)) {
      setParsedData({
        rows: receivedReferrals.rows.map(item => ({
          ...item,
          user: currentUser,
          referral: new Referral(item),
          actions: {
            onViewNotes: () => handleOnViewNotes(item.id),
            onReview: () => handleOnReview(item.id, item),
            onComplete: () => handleOnComplete(item),
            onSetAppointment: () => handleSetAppointment(item.id, item.appointment, item.status),
            onUpdateReferral: () => handleUpdateReferral(item),
            onUpdateReferrerClinic: () => handleUpdateReferrerClinic(item),
          },
        })),
        count: receivedReferrals.rows.length,
      });
    }
  }, [receivedReferrals, currentUser, handleOnViewNotes, handleOnReview, handleOnComplete, handleSetAppointment, handleUpdateReferral, handleUpdateReferrerClinic]);

  // ---------------------------
  // SUBSCRIPTION REFRESH
  // ---------------------------
  useEffect(() => {
    if (!isEmpty(referrals)) {
      if (!isEmpty(referralsSubscribeRef.current) &&
        JSON.stringify(referrals) !== JSON.stringify(referralsSubscribeRef.current)) {
        refetchData(searchFilters);
      }
      referralsSubscribeRef.current = referrals;
    }
  }, [referrals, refetchData, searchFilters]);

  // ---------------------------
  // RENDER
  // ---------------------------
  if (loading) return <GlobalLoader />;

  return (
    <>
      {error && !closeError && (
        <Alert
          variant="filled"
          severity="error"
          onClose={() => setCloseError(true)}
        >
          We are sorry, but we are having difficulty with our system. We are unable to load data at
          the moment. Please reach out to us if you continue to experience this issue.
        </Alert>
      )}

      <ActiveReceivedReferralsTable
        onFilterChanged={handleFilterChanged}
        onChangeDropdown={handleChangeDropdown}
        onSelectedRowsChanged={() => { }}
        loading={loading}
        data={!isEmpty(parsedData) ? parsedData : { rows: [], count: 0 }}
      />

      <DialogReferralNotes
        referralUuid={referralUuid}
        openNotes={openNotes}
        handleClose={() => setOpenNotes(false)}
        isExternal={false}
      />

      <SetAppointmentReferralDialog
        onOpen={openAppointmentDialog}
        onClose={() => { setOpenAppointmentDialog(false); refetchData(searchFilters); }}
        history={history}
        uuid={referralUuid}
        status={status}
        appointmentDate={scheduleAppointmentDate}
        reschedule={Boolean(scheduleAppointmentDate)}
      />

      <UpdateMiniReferralDialog
        open={openReferralDialog}
        onClose={() => { setOpenReferralDialog(false); refetchData(searchFilters); }}
        referral={referral}
        isPublicPatient={true}
        isReceived={true}
      />

      <ReferralCompleteDialog
        initialAppointmentDate={appointmentDate}
        initialOpen={openCompleteDialog}
        referralUuid={referralUuid}
        onPromoteReferral={onPromoteReferral}
        onClose={() => setOpenCompleteDialog(false)}
      />

      <UpdateReferrerClinicDialog
        open={openReferrerClinicDialog}
        onClose={() => { setOpenReferrerClinicDialog(false); refetchData(searchFilters); }}
        referral={referral}
      />
    </>
  );
}
